import React from "react";
import styled from "styled-components";
import { ScheduledPost } from "../../../../../database/community/scheduledPosts";
import { platformLabel } from "../../publish/SchedulePostDialog";
import { useMediaStudio } from "../../context/MediaStudioContext";
import { ExternalLink, Muted, Row, RowList, Thumb, projectThumb } from "../shared";
import { compact, timeLabel } from "./homeDerive";

// Body of the post detail modal: caption, per-platform metrics, and the
// public link when the publisher stored one.

const Caption = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  line-height: 1.5;
`;

const MetricName = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  text-transform: capitalize;
  min-width: 84px;
`;

export type PostDetailPanelProps = {
  post: ScheduledPost;
};

export const PostDetailPanel: React.FC<PostDetailPanelProps> = ({ post }) => {
  const { projects } = useMediaStudio();

  const thumb =
    projectThumb(
      post.projectId ? projects.projectById.get(post.projectId) : undefined
    ) || post.mediaUrls[0];

  const platforms = Object.keys(post.metrics || {});
  const linkFor = (platform: string): string | undefined => {
    const url = post.results?.[platform]?.url;
    return typeof url === "string" && url.startsWith("http") ? url : undefined;
  };

  return (
    <>
      <div style={{ display: "flex", gap: "var(--space-3)" }}>
        {thumb ? (
          <Thumb src={thumb} alt="" style={{ width: 56, height: 70 }} />
        ) : null}
        <div style={{ minWidth: 0, flex: 1 }}>
          <Caption>{post.caption?.trim() || "Untitled post"}</Caption>
          <Muted>{timeLabel(post.dueAt)}</Muted>
        </div>
      </div>
      <RowList>
        {platforms.map((platform) => {
          const m = post.metrics?.[platform] || {};
          const url = linkFor(platform);
          return (
            <Row key={platform}>
              <MetricName>{platformLabel(platform)}</MetricName>
              <Muted>
                {[
                  typeof m.reach === "number" && `${compact(m.reach)} reach`,
                  typeof m.views === "number" && `${compact(m.views)} views`,
                  typeof m.likes === "number" && `${compact(m.likes)} likes`,
                  typeof m.comments === "number" &&
                    `${compact(m.comments)} comments`,
                  typeof m.saved === "number" && `${compact(m.saved)} saves`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Muted>
              <span style={{ flex: 1 }} />
              {url ? (
                <ExternalLink href={url}>
                  View on {platformLabel(platform)}
                </ExternalLink>
              ) : (
                <Muted>No public link stored</Muted>
              )}
            </Row>
          );
        })}
        {platforms.length === 0 && (
          <Row>
            <Muted>No metrics recorded for this post yet.</Muted>
          </Row>
        )}
      </RowList>
    </>
  );
};

export default PostDetailPanel;
